<script lang="ts">
	import { Section, SectionHeader, type SectionTheme } from "#lib/web-ui/primitives/index.js";
	import ArrowRightIcon from "@lucide/svelte/icons/arrow-right";
	import { cloudinaryUrl, cloudinarySrcset } from "#lib/shared/cloudinary.js";

	interface Props {
		theme?: SectionTheme;
	}

	let { theme = "light" }: Props = $props();

	let activeIndex = $state(0);

	const actions = [
		{
			label: "Invest",
			description:
				"Access institutional-quality syndications and private equity offerings alongside our own capital.",
			services:
				"Syndications, Private Equity, Passive Income, Institutional-Quality Offerings",
			imageId: "fergmar-com/skyscrapers-hero",
			href: "https://fergmarcapital.com",
			external: true
		},
		{
			label: "Rent",
			description:
				"Browse apartments, homes, and townhomes across our managed communities in South Texas.",
			services: "Apartments, Homes, Townhomes, Managed Communities, Short & Long-Term",
			imageId: "fergmar-com/apartment-interior",
			href: "/properties/for-rent",
			external: false
		},
		{
			label: "Lease",
			description:
				"Find commercial, retail, industrial, and storage space for your business across our managed portfolio.",
			services: "Office, Retail, Industrial, Storage, NNN Leases, Commercial Spaces",
			imageId: "fergmar-com/modern-office",
			href: "/properties/for-lease",
			external: false
		},
		{
			label: "Buy",
			description:
				"Explore remodeled homes, new construction, and investment properties available for purchase.",
			services:
				"Remodeled Homes, New Construction, Investment Properties, Multi-Family, Commercial",
			imageId: "fergmar-com/suburban-house",
			href: "/properties/for-sale",
			external: false
		},
		{
			label: "Sell",
			description:
				"Get a competitive cash offer, full-service listing with marketing support, or protect your home equity.",
			services:
				"Cash Offers, Listing Services, Marketing & Staging, Equity Protection Program",
			imageId: "fergmar-com/house-for-sale",
			href: "https://fergmarhomebuyers.com",
			external: true
		}
	];
</script>

<Section {theme} class="!py-20 md:!py-28">
	<SectionHeader label="Services" title="How Fergmar Can Help You" align="left" class="mb-14" />

	<!-- Mobile: vertical card stack -->
	<div class="flex flex-col gap-3 md:hidden">
		{#each actions as action, i (action.href)}
			<a
				href={action.href}
				target={action.external ? "_blank" : undefined}
				rel={action.external ? "noopener noreferrer" : undefined}
				class="group relative flex h-52 overflow-hidden rounded-lg"
			>
				<img
					src={cloudinaryUrl(action.imageId, { width: 600 })}
					srcset={cloudinarySrcset(action.imageId, 400)}
					sizes="100vw"
					alt=""
					class="absolute inset-0 h-full w-full object-cover"
					width="600"
					height="208"
					loading="lazy"
				/>
				<!-- bg-black is intentionally hardcoded — bg-foreground inverts to white in dark-themed sections -->
				<div class="absolute inset-0 bg-black/85"></div>

				<div class="dark relative flex w-full flex-col justify-end p-5">
					<div class="flex items-center justify-between">
						<h3 class="text-foreground text-2xl font-bold tracking-tight">
							{action.label}
						</h3>
						<ArrowRightIcon
							class="text-foreground size-5 transition-transform group-hover:translate-x-1"
							strokeWidth={1.5}
						/>
					</div>
					<p class="text-muted-foreground mt-1.5 text-sm leading-relaxed">
						{action.description}
					</p>
					<div class="mt-2.5 flex flex-wrap gap-1.5">
						{#each action.services.split(", ") as tag (tag)}
							<span
								class="border-foreground/25 text-foreground/70 rounded-full border px-2 py-0.5 text-[11px] whitespace-nowrap"
							>
								{tag}
							</span>
						{/each}
					</div>
				</div>
			</a>
		{/each}
	</div>

	<!--
		Desktop: Fixed-height accordion — prevents layout shift.
		flex-[3]/flex-1 distributes the fixed height; description is absolutely
		positioned so variable text lengths never affect row height.
	-->
	<div class="hidden h-[740px] md:flex md:flex-col">
		{#each actions as action, i (action.href)}
			{@const isActive = activeIndex === i}
			<a
				href={action.href}
				target={action.external ? "_blank" : undefined}
				rel={action.external ? "noopener noreferrer" : undefined}
				class="group border-foreground/10 relative flex overflow-hidden border-t transition-all duration-500 ease-in-out {isActive
					? 'flex-[2]'
					: 'flex-1'}"
				onmouseenter={() => (activeIndex = i)}
			>
				<!-- Background image (only visible when active) -->
				<div
					class="absolute inset-0 transition-opacity duration-700 {isActive
						? 'opacity-100'
						: 'opacity-0'}"
				>
					<img
						src={cloudinaryUrl(action.imageId, { width: 1200 })}
						srcset={cloudinarySrcset(action.imageId, 600)}
						sizes="100vw"
						alt=""
						class="h-full w-full object-cover"
						width="1200"
						height="740"
						loading="lazy"
					/>
					<!-- bg-black is intentionally hardcoded — bg-foreground inverts to white in dark-themed sections -->
					<div class="absolute inset-0 bg-black/75"></div>
				</div>

				<!-- Content — dark scope when active forces semantic tokens to light-on-dark -->
				<div class="relative flex w-full items-center gap-6 px-4 {isActive ? 'dark' : ''}">
					<!-- Number -->
					<div
						class="border-foreground/75 text-foreground/85 flex size-9 shrink-0 items-center justify-center rounded-full border text-sm"
					>
						{i + 1}
					</div>

					<!-- Description: locked to vertical center, never moves -->
					<div class="absolute top-1/2 left-16 max-w-[380px] -translate-y-1/2">
						<p class="text-muted-foreground text-sm leading-relaxed">
							{action.description}
						</p>
					</div>

					<!-- Tags: fixed below center line, independent of description height -->
					<div
						class="absolute top-[calc(50%+1.75rem)] left-16 max-w-[380px] transition-all duration-500 {isActive
							? 'opacity-100'
							: 'pointer-events-none opacity-0'}"
					>
						<div class="flex flex-wrap gap-1.5">
							{#each action.services.split(", ") as tag (tag)}
								<span
									class="border-foreground/25 text-foreground/70 rounded-full border px-2.5 py-0.5 text-xs whitespace-nowrap"
								>
									{tag}
								</span>
							{/each}
						</div>
					</div>

					<!-- Large label -->
					<div class="ml-auto flex shrink-0 items-center gap-6 sm:gap-10">
						<span
							class="text-foreground text-4xl font-bold tracking-tight transition-all duration-500 sm:text-6xl md:text-7xl {isActive
								? 'opacity-100'
								: 'opacity-70'}"
						>
							{action.label}
						</span>

						<!-- Arrow (only when active) -->
						<div
							class="transition-all duration-500 {isActive
								? 'translate-x-0 opacity-100'
								: 'translate-x-4 opacity-0'}"
						>
							<ArrowRightIcon
								class="text-foreground size-8 sm:size-10"
								strokeWidth={1.5}
							/>
						</div>
					</div>
				</div>
			</a>
		{/each}
		<!-- Bottom border -->
		<div class="border-foreground/10 border-t"></div>
	</div>
</Section>
